import { ref, readonly, Component, computed } from 'vue';
import { useAuthentication } from '@/composables/useAuthentication';
import { useModals } from '@/composables/useModals';
import { useUrlParameters } from '@/composables/useUrlParameters';
import AdvancedAlertsModeUI from '@/components/mode-specific-ui/AdvancedAlertsModeUI.vue';
import ChartCommentsModeUI from '@/components/mode-specific-ui/ChartCommentsModeUI.vue';
import ChartLibraryModeUI from '@/components/mode-specific-ui/ChartLibraryModeUI.vue';
import ChartListSummaryModeUI from '@/components/mode-specific-ui/ChartListSummaryModeUI.vue';
import ChartSettingsModeUI from '@/components/mode-specific-ui/ChartSettingsModeUI.vue';
import ComingSoonModeUI from '@/components/mode-specific-ui/ComingSoonModeUI.vue';
import HistoricalPriceDataModeUI from '@/components/mode-specific-ui/HistoricalPriceDataModeUI.vue';
import OptionsModeUI from '@/components/mode-specific-ui/OptionsModeUI.vue';
import PredefinedAlertsModeUI from '@/components/mode-specific-ui/PredefinedAlertsModeUI.vue';
import PriceAlertsModeUI from '@/components/mode-specific-ui/PriceAlertsModeUI.vue';
import { useSymbolInformation } from '@/composables/useSymbolInformation';

interface Mode {
  title: string;
  id: string;
  uiComponent: Component;
  icon: string;
  uiHtmlId: string;
  isPayingMemberOnly: boolean;
  isUnavailableForInvalidSymbols: boolean;
}

const ADVANCED_ALERTS_MODE: Mode = {
  title: 'Advanced Alerts',
  id: 'yourAdvancedAlertsMode',
  uiComponent: AdvancedAlertsModeUI,
  icon: 'far fa-fw fa-bell',
  uiHtmlId: 'advanced-alerts-mode-ui',
  isPayingMemberOnly: true,
  isUnavailableForInvalidSymbols: false
};
const CHART_SETTINGS_MODE: Mode = {
  title: 'Chart Settings',
  id: 'chartSettingsMode',
  uiComponent: ChartSettingsModeUI,
  icon: 'fas fa-fw fa-cog',
  uiHtmlId: 'chart-settings-mode-ui',
  isPayingMemberOnly: false,
  isUnavailableForInvalidSymbols: false
};
const CHART_STYLE_LIBRARY_MODE: Mode = {
  title: 'ChartStyle Library',
  id: 'chartStyleLibraryMode',
  uiComponent: ChartLibraryModeUI,
  icon: 'fas fa-fw fa-images',
  uiHtmlId: 'chart-style-library-mode-ui',
  isPayingMemberOnly: false,
  isUnavailableForInvalidSymbols: true
};
const CHART_LIST_SUMMARY_MODE: Mode = {
  title: 'ChartList Summary',
  id: 'chartListSummaryMode',
  uiComponent: ChartListSummaryModeUI,
  icon: 'fal fa-fw fa-table',
  uiHtmlId: 'chart-list-summary-ui-container',
  isPayingMemberOnly: false,
  isUnavailableForInvalidSymbols: false
};
const CHART_COMMENTS_MODE: Mode = {
  title: 'Chart Comments',
  id: 'chartCommentsMode',
  uiComponent: ChartCommentsModeUI,
  icon: 'far fa-fw fa-comment-lines',
  uiHtmlId: 'chart-comments-mode-ui',
  isPayingMemberOnly: false,
  isUnavailableForInvalidSymbols: true
};
const CHART_LIST_LIBRARY_MODE: Mode = {
  title: 'ChartList Library',
  id: 'chartListLibraryMode',
  uiComponent: ChartLibraryModeUI,
  icon: 'fas fa-fw fa-th',
  uiHtmlId: 'chart-list-library-mode-ui',
  isPayingMemberOnly: false,
  isUnavailableForInvalidSymbols: false
};
const OPTIONS_MODE: Mode = {
  title: 'Options',
  id: 'optionsMode',
  uiComponent: OptionsModeUI,
  icon: 'fas fa-fw fa-game-board-alt',
  uiHtmlId: 'options-mode-ui',
  isPayingMemberOnly: false,
  isUnavailableForInvalidSymbols: true
};
const SYMBOL_SUMMARY_MODE: Mode = {
  title: 'Symbol Summary',
  id: 'symbolSummaryMode',
  uiComponent: ComingSoonModeUI,
  icon: 'far fa-fw fa-file-invoice-dollar',
  uiHtmlId: 'symbol-summary-mode-ui',
  isPayingMemberOnly: false,
  isUnavailableForInvalidSymbols: true
};
const HISTORICAL_PRICE_DATA_MODE: Mode = {
  title: 'Historical Price Data',
  id: 'historicalPriceDataMode',
  uiComponent: HistoricalPriceDataModeUI,
  icon: 'far fa-fw fa-hourglass-end',
  uiHtmlId: 'price-data-mode-ui',
  isPayingMemberOnly: true,
  isUnavailableForInvalidSymbols: true
};
const PREDEFINED_ALERTS_MODE: Mode = {
  title: 'Predefined Alerts',
  id: 'yourPredefinedAlertsMode',
  uiComponent: PredefinedAlertsModeUI,
  icon: 'far fa-fw fa-bells',
  uiHtmlId: 'predefined-alerts-mode-ui',
  isPayingMemberOnly: true,
  isUnavailableForInvalidSymbols: false
};
const PRICE_ALERTS_MODE: Mode = {
  title: 'Price Alerts',
  id: 'yourPriceAlertsMode',
  uiComponent: PriceAlertsModeUI,
  icon: 'far fa-fw fa-usd-circle',
  uiHtmlId: 'price-alerts-mode-ui',
  isPayingMemberOnly: true,
  isUnavailableForInvalidSymbols: false
};

const DEFAULT_MODE = CHART_SETTINGS_MODE;

const MODE_GROUPS = [
  {
    groupTitle: 'Settings',
    modes: [
      CHART_SETTINGS_MODE,
      CHART_STYLE_LIBRARY_MODE
    ]
  },
  {
    groupTitle: 'Saved Charts',
    modes: [
      CHART_LIST_SUMMARY_MODE,
      CHART_COMMENTS_MODE,
      CHART_LIST_LIBRARY_MODE
    ]
  },
  {
    groupTitle: 'Alerts',
    modes: [
      PRICE_ALERTS_MODE,
      ADVANCED_ALERTS_MODE,
      PREDEFINED_ALERTS_MODE
    ]
  },
  {
    groupTitle: 'Tools & Resources',
    modes: [
      OPTIONS_MODE,
      SYMBOL_SUMMARY_MODE,
      HISTORICAL_PRICE_DATA_MODE
    ]
  }
];
const MODES_ARRAY: Mode[] = [];

for (const modeGroup of MODE_GROUPS) {
  for (const mode of modeGroup.modes) {
    MODES_ARRAY.push(mode);
  }
}

const currentModeId = ref(DEFAULT_MODE.id);

const OPTIONS_PLAY_WIDGET_UI_BELOW_CHART_OPTION = 'optionsPlay';

type OptionsModeView = 'straddleView' | 'listView' | 'optionsPlayView';
const OPTIONS_MODE_STRADDLE_VIEW: OptionsModeView = 'straddleView';
const OPTIONS_MODE_LIST_VIEW: OptionsModeView = 'listView';
const OPTIONS_MODE_OPTIONS_PLAY_VIEW: OptionsModeView = 'optionsPlayView';
const optionsModeView = ref<OptionsModeView>(OPTIONS_MODE_STRADDLE_VIEW);

const currentMode = computed(() => {
  return MODES_ARRAY.find(mode => mode.id === currentModeId.value) || null;
});

function setOptionsModeView(view: OptionsModeView) {
  optionsModeView.value = view;
}

function initializeUIBelowChartBasedOnUrlParameters() {
  const { getUIBelowChartUrlParameterValue } = useUrlParameters();

  const urlQueryString = window.location.search;
  const uiBelowChartUrlParameterValue = getUIBelowChartUrlParameterValue(urlQueryString);

  if (uiBelowChartUrlParameterValue === OPTIONS_PLAY_WIDGET_UI_BELOW_CHART_OPTION) {
    window.sc3WorkbenchLogger.info('Attempting to initialize the UI below the chart to be the OptionsPlay widget...');

    currentModeId.value = OPTIONS_MODE.id;
    optionsModeView.value = OPTIONS_MODE_OPTIONS_PLAY_VIEW;

    window.sc3WorkbenchLogger.info('Successfully initialized the UI below the chart to be the OptionsPlay widget.');
  } else {
    window.sc3WorkbenchLogger.info(`Unable to initialize the UI below the chart - no valid URL parameter supplied: ${urlQueryString}.`);
  }
}

function setMode(mode: Mode) {
  const { isLoggedIn } = useAuthentication();
  const { launchCreateAccountModal } = useModals();
  const { isSymbolBeingViewedNotFound } = useSymbolInformation();

  if (mode.isPayingMemberOnly && !isLoggedIn.value) {
    launchCreateAccountModal();
    return;
  }
  if (mode.isUnavailableForInvalidSymbols && isSymbolBeingViewedNotFound.value) {
    return;
  }

  currentModeId.value = mode.id;
}

export function useModes() {
  return {
    ADVANCED_ALERTS_MODE,
    CHART_SETTINGS_MODE,
    CHART_STYLE_LIBRARY_MODE,
    CHART_LIST_SUMMARY_MODE,
    CHART_COMMENTS_MODE,
    CHART_LIST_LIBRARY_MODE,
    OPTIONS_MODE,
    SYMBOL_SUMMARY_MODE,
    HISTORICAL_PRICE_DATA_MODE,
    PREDEFINED_ALERTS_MODE,
    PRICE_ALERTS_MODE,
    MODE_GROUPS,
    MODES_ARRAY,
    currentModeId: readonly(currentModeId),
    currentMode,
    OPTIONS_PLAY_WIDGET_UI_BELOW_CHART_OPTION,
    OPTIONS_MODE_STRADDLE_VIEW,
    OPTIONS_MODE_LIST_VIEW,
    OPTIONS_MODE_OPTIONS_PLAY_VIEW,
    optionsModeView: readonly(optionsModeView),
    setOptionsModeView,
    initializeUIBelowChartBasedOnUrlParameters,
    setMode
  };
}
